import i18n from '../../../i18n';
import type { NorthStarSampleBasis, NorthStarWindow, PerfSettings } from '../types';
import { fmtInt } from './growthFormat';
import {
  compareFrameLabel,
  effectiveCompareKind,
  formatRangeLabel,
  frameLabel,
  isDateRange,
} from './perfSettings';

function t(key: string, options?: Record<string, unknown>): string {
  return i18n.t(`performance:nsDrill.${key}`, options);
}

/**
 * The one window every number on the North Star drill reads, named once:
 * "Last 4 weeks vs same weekdays last year". The settings are the
 * Performance view's; the drill's echoed window, when there, supplies the
 * comparison dates of a date range.
 */
export function northStarWindowLabel(
  settings: PerfSettings,
  window?: NorthStarWindow | null,
): string {
  const period = frameLabel(settings);
  const kind = window?.compareKind ?? effectiveCompareKind(settings);
  if (kind === 'off' || (window && !window.compare)) {
    return t('windowNoCompare', { period });
  }
  let compare: string;
  if (kind === 'prior') {
    compare = t('compare.prior');
  } else if ((kind === 'yoy' || kind === 'yoy_weekday') && !isDateRange(settings)) {
    compare = t(`compare.${kind}`);
  } else if (window?.compare) {
    compare = formatRangeLabel(window.compare.start, window.compare.end);
  } else {
    compare = compareFrameLabel(settings) ?? t('compare.prior');
  }
  return t('windowLine', { period, compare });
}

/** The exact dates behind the window, for its hover title. */
export function northStarWindowDates(window?: NorthStarWindow | null): string | undefined {
  if (!window) return undefined;
  const period = formatRangeLabel(window.start, window.end);
  return window.compare
    ? t('windowDates', {
        period,
        compare: formatRangeLabel(window.compare.start, window.compare.end),
      })
    : period;
}

/** "n = 1,234 reservations": what a rate or average component rests on. */
export function northStarSampleLabel(
  n: number | undefined,
  basis: NorthStarSampleBasis | undefined,
): string | null {
  if (n == null || !Number.isFinite(n)) return null;
  return t(`sample.${basis ?? 'reservations'}`, { count: n, n: fmtInt(n) });
}
